<script lang="ts">
  import { untrack } from "svelte";
  import type { HTMLInputAttributes } from "svelte/elements";
  import { Input } from "./index.js";
  import { locale } from "$lib/i18n";

  type Props = Omit<HTMLInputAttributes, "type" | "value" | "files" | "oninput"> & {
    value: number;
  };

  let { value = $bindable(), ...restProps }: Props = $props();

  /** Decimal separator of the app locale — not the browser locale. */
  const separator = $derived($locale === "de" ? "," : ".");

  /** Keeps digits and at most one separator; everything else is dropped. */
  function sanitize(raw: string, sep: string): string {
    let result = "";
    let separatorSeen = false;
    for (const char of raw) {
      if (char >= "0" && char <= "9") {
        result += char;
      } else if ((char === "." || char === ",") && !separatorSeen) {
        separatorSeen = true;
        result += sep;
      }
    }
    return result;
  }

  function parse(text: string, sep: string): number {
    if (text === "" || text === sep) return 0;
    return Number(text.replace(sep, "."));
  }

  function format(num: number, sep: string): string {
    if (num === undefined || num === null || Number.isNaN(num)) return "";
    return String(num).replace(".", sep);
  }

  let text = $state(untrack(() => format(value, separator)));

  // Re-sync the displayed text when the bound value or the locale changes from
  // the outside. Editing does not run this — the text already matches then.
  $effect(() => {
    const currentValue = value;
    const sep = separator;
    untrack(() => {
      if (parse(text, sep) !== currentValue || text !== sanitize(text, sep)) {
        text = format(currentValue, sep);
      }
    });
  });

  function handleInput(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const sanitized = sanitize(element.value, separator);
    if (sanitized !== element.value) {
      const caret = (element.selectionStart ?? sanitized.length) - (element.value.length - sanitized.length);
      element.value = sanitized;
      element.setSelectionRange(caret, caret);
    }
    text = sanitized;
    value = parse(sanitized, separator);
  }
</script>

<Input type="text" inputmode="decimal" bind:value={text} oninput={handleInput} {...restProps} />
